import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Button, Grid, IconButton } from '@mui/material';
import Container from '../Layout/Container';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import TableGridItem from '../UI/TableGridItem';
import GridContainer from '../Layout/GridContainer';
import { ColDef, EditableCallbackParams, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react/dist/types/src/agGridReact';
import { AgGridReactProps } from 'ag-grid-react';
import DeleteIcon from '@mui/icons-material/Delete';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';

interface CRUDSettingsProps extends AgGridReactProps {
    tableRows: any[];
    columnDefs: ColDef[];
    createRecord?: (record: any) => void;
    updateRecord?: (record: any) => void;
    getAllData?: () => void;
    hideButton?: boolean;
    btnText: string;
    recordStructure?: { [key: string]: any };
    recordType: string;
    validateData?: (record: any) => boolean;
    isCreationViaDialog?: boolean;
    isEditViaDialog?: boolean;
    setShowCreationDialog?: (show: boolean, isEdit?: boolean) => void;
    onSelectedRowChange?: (record: any) => void;
    filters?: FC<{ onCreate: () => void }>;
    hideIdColumn?: boolean;
    showDeleteButton?: boolean;
    onDelete?: (record: any, index: number | null) => void;
    columnToFocusOnEdit?: string;
    hideSaveButton?: boolean;
    height?: string;
    sx?: SxProps<Theme>;
    style?: React.CSSProperties;
    unshiftCallback?: (row: any) => void;
    onChangeRow?: (row: any, index: number | null) => void;
}

const CRUDSettings: FC<CRUDSettingsProps> = ({
    tableRows,
    columnDefs,
    createRecord,
    updateRecord,
    getAllData,
    hideButton,
    btnText,
    recordStructure,
    recordType,
    validateData,
    isCreationViaDialog,
    isEditViaDialog,
    setShowCreationDialog,
    onSelectedRowChange,
    filters,
    hideIdColumn,
    showDeleteButton,
    onDelete,
    columnToFocusOnEdit,
    hideSaveButton,
    height,
    sx,
    style,
    unshiftCallback,
    onChangeRow,
    ...restTableSettings
}) => {
    const gridRef = useRef<AgGridReact>();

    columnToFocusOnEdit = columnToFocusOnEdit || columnDefs[0].field;

    const addEmptyRowToTopOfTable = useCallback(() => {
        const _createdAt = new Date().toISOString();
        const emptyRow = {
            id: undefined,
            name: undefined,
            isNewRecord: true,
            _createdAt,
            ...recordStructure,
        };

        if (gridRef?.current?.api) {
            let index = 0;
            if (unshiftCallback) {
                unshiftCallback(emptyRow);
            } else {
                gridRef.current.api.applyTransaction({
                    add: [emptyRow],
                    addIndex: index,
                });
            }
            setTimeout(() => {
                if (unshiftCallback) {
                    index =
                        gridRef?.current?.api
                            .getRenderedNodes()
                            .findIndex(({ data }) => data._createdAt === _createdAt) || 0;
                    if (index === -1) index = 0;
                }
                gridRef?.current?.api.setFocusedCell(index, String(columnToFocusOnEdit));
                gridRef?.current?.api.startEditingCell({
                    rowIndex: index,
                    colKey: String(columnToFocusOnEdit),
                });
            }, 20);
        }
    }, [recordStructure, unshiftCallback, columnToFocusOnEdit]);

    const startEditingRecord = useCallback((rowIndex: number, columnToFocusOnEdit: any) => {
        if (gridRef?.current?.api) {
            gridRef.current.api.setFocusedCell(rowIndex, columnToFocusOnEdit);
            gridRef.current.api.startEditingCell({
                rowIndex,
                colKey: columnToFocusOnEdit,
            });
        }
    }, []);

    const handleRowEdit = useCallback(
        (params: ICellRendererParams) => {
            if (isEditViaDialog) {
                if (onSelectedRowChange) onSelectedRowChange(params.data);
                if (setShowCreationDialog) setShowCreationDialog(true, true);
                return;
            }

            const { rowIndex } = params.node;
            params.node.setData({ ...params.node.data, isRowEdited: true });

            if (rowIndex || rowIndex === 0) startEditingRecord(rowIndex, columnToFocusOnEdit);
        },
        [startEditingRecord, columnToFocusOnEdit, isEditViaDialog, onSelectedRowChange, setShowCreationDialog]
    );

    const updateExistingRecord = useCallback(
        (params: ICellRendererParams) => {
            let isDataValid = true;
            if (validateData) isDataValid = validateData(params.data);
            if (isDataValid && updateRecord) updateRecord(params.data);
        },
        [updateRecord, validateData]
    );

    const postRecord = useCallback(
        (params: ICellRendererParams) => {
            const { isNewRecord } = params.data;
            if (isNewRecord) {
                if (createRecord) createRecord(params.data);
            } else {
                updateExistingRecord(params);
            }
        },
        [createRecord, updateExistingRecord]
    );

    const deleteRecord = useCallback(
        (params: ICellRendererParams) => {
            if (params.data.isNewRecord && !unshiftCallback) {
                gridRef?.current?.api.applyTransaction({
                    remove: [params.data],
                });
            } else if (onDelete) {
                onDelete(params.data, params.node.rowIndex);
            }
        },
        [onDelete, unshiftCallback]
    );

    const handleCreateButtonClick = () => {
        if (isCreationViaDialog) {
            if (isEditViaDialog) {
                if (onSelectedRowChange) onSelectedRowChange(null);
            }

            if (setShowCreationDialog) setShowCreationDialog(true);
        } else {
            addEmptyRowToTopOfTable();
        }
    };

    const columns = useMemo((): ColDef[] => {
        return ([] as ColDef[])
            .concat(
                !hideIdColumn
                    ? {
                          field: 'id',
                          headerName: 'ID',
                          maxWidth: 100,
                          editable: (params: EditableCallbackParams) => params.data.isNewRecord,
                      }
                    : []
            )
            .concat(...columnDefs)
            .concat({
                field: '',
                headerName: 'Edit',
                editable: false,
                filter: false,
                sortable: false,
                maxWidth: 68,
                suppressHeaderMenuButton: true,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <>
                            {!params.data.isNewRecord && (
                                <IconButton aria-label="Edit" size="small" onClick={() => handleRowEdit(params)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}

                            {!hideSaveButton && (params.data.isRowEdited || params.data.isNewRecord) && (
                                <IconButton aria-label="Save" size="small" onClick={() => postRecord(params)}>
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            )}
                            {showDeleteButton && (!params.data.isRowEdited || params.data.isNewRecord) && (
                                <IconButton aria-label="Delete" size="small" onClick={() => deleteRecord(params)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </>
                    );
                },
            });
    }, [hideIdColumn, columnDefs, hideSaveButton, showDeleteButton, handleRowEdit, postRecord, deleteRecord]);

    const onCellValueChanged = ({ node }: CellValueChangedEvent) => {
        if (onChangeRow) {
            onChangeRow({ ...node.data, isRowEdited: true }, node.rowIndex);
        } else {
            node.setData({ ...node.data, isRowEdited: true });
        }
    };

    function withProps(Component: React.FC<any>) {
        return function WrappedComponent(props: any) {
            return <Component {...props} />;
        };
    }

    const FiltersComponent = filters ? withProps(filters) : undefined;

    return (
        <Container wide={false} style={style}>
            <GridContainer sx={sx}>
                {FiltersComponent && <FiltersComponent onCreate={handleCreateButtonClick} />}
                {!hideButton && (
                    <Grid item xs={3}>
                        <Button onClick={handleCreateButtonClick} variant="contained">
                            {btnText}
                        </Button>
                    </Grid>
                )}
                <TableGridItem
                    ref={gridRef}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: true,
                        sortable: true,
                    }}
                    columnDefs={columns}
                    height={height}
                    onCellValueChanged={onCellValueChanged}
                    {...restTableSettings}
                />
            </GridContainer>
        </Container>
    );
};

export default CRUDSettings;
